import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from '@apollo/react-hooks';
import { useEffect } from 'react';
import gql from 'graphql-tag';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive';
import { fg } from '@confluence/feature-gating';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution';
import { expValEquals } from '@confluence/feature-experiments';
import { useNetworkStatusListenerCache } from '@confluence/legacy-schema-bridge/entry-points/useNetworkStatusListenerCache';

// typsy type generation currently does not support queries from ClientSchema such as this one
import { ClientNetworkStatus } from './__types__/useNativeCollabConnectionListenerQuery';

export type useNativeCollabConnectionListenerProps = {
	collabEditConnectionTimedOut: boolean;
	collabInitTimedOut: boolean;
};

const useNativeCollabConnectionListenerQuery = gql`
	query useNativeCollabConnectionListenerQuery {
		network {
			status
		}
	}
`;

export const useNativeCollabConnectionListener = ({
	collabEditConnectionTimedOut,
	collabInitTimedOut,
}: useNativeCollabConnectionListenerProps) => {
	const isLivePage = useIsCurrentPageLive();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	// eslint-disable-next-line graphql-client-usage/graphql-generic-types
	const { data, error } = useQuery(useNativeCollabConnectionListenerQuery, {
		fetchPolicy: 'cache-only',
		skip:
			!fg('confluence_analytics_on_ncs_disconnect') ||
			expValEquals('confluence_replace_apollo_client_schema', 'isEnabled', true),
	});

	const networkStatus = useNetworkStatusListenerCache();

	const status = expValEquals('confluence_replace_apollo_client_schema', 'isEnabled', true)
		? networkStatus
		: data?.network?.status;

	useEffect(() => {
		if (error) {
			markErrorAsHandled(error);
			getMonitoringClient().submitError(error, {
				attribution: Attribution.PAGE_MODES,
			});
		}
	}, [error]);

	useEffect(() => {
		if (!fg('confluence_analytics_on_ncs_disconnect')) {
			return;
		}

		const networkOffline =
			status === ClientNetworkStatus.IP_BLOCKED || status === ClientNetworkStatus.OFFLINE;
		const isCollabDisconnected = collabEditConnectionTimedOut || collabInitTimedOut;

		if (!networkOffline && isCollabDisconnected) {
			// If NCS times out, but user is still connected to network
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					source: 'nativeCollabComponent',
					actionSubject: 'nativeCollabConnectionListener',
					actionSubjectId: 'nativeCollabConnectionListener',
					action: 'disconnected',
					attributes: {
						isLivePage,
					},
				},
			}).fire();
		}
	}, [status, collabEditConnectionTimedOut, isLivePage, collabInitTimedOut, createAnalyticsEvent]);
};
